import React, { useState, useEffect, useCallback } from 'react';
import { api, getApiErrorMessage } from '../lib/api';

const LEVEL_COLORS = {
  NOMINAL: '#00E5FF',
  ELEVATED: '#FF9F0A',
  STORM: '#FF6B35',
  EXTREME: '#FF3B30',
};

const DOMAIN_LABELS = {
  space_weather: 'Space Weather',
  solar_activity: 'Solar Activity',
  orbital_tracking: 'Orbital Tracking',
  astronomy_events: 'Astronomy Events',
  cosmic_background: 'Cosmic Background',
};

const GEN_STATES = { IDLE: 'idle', GENERATING: 'generating', GENERATED: 'generated', FAILED: 'failed' };

export default function OrionRightPanel({ insight, insights, selectedInsightId, onSelectInsight, scoring }) {
  const [assessment, setAssessment] = useState(null);
  const [script, setScript] = useState(null);
  const [genState, setGenState] = useState(GEN_STATES.IDLE);
  const [copied, setCopied] = useState(false);
  const [compactView, setCompactView] = useState(false);

  useEffect(() => {
    if (!selectedInsightId) {
      setAssessment(null);
      setScript(null);
      setGenState(GEN_STATES.IDLE);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/api/orion/assess/insight/${selectedInsightId}`);
        if (!cancelled) setAssessment(res.data);
      } catch {
        if (!cancelled) setAssessment(null);
      }
    })();
    setScript(null);
    setGenState(GEN_STATES.IDLE);
    return () => { cancelled = true; };
  }, [selectedInsightId]);

  const handleGenerateScript = useCallback(async () => {
    if (!selectedInsightId || genState === GEN_STATES.GENERATING) return;
    setGenState(GEN_STATES.GENERATING);
    try {
      const res = await api.post(`/api/orion/generate-script/insight/${selectedInsightId}`, null, { timeout: 60000 });
      setScript(res.data);
      setGenState(GEN_STATES.GENERATED);
    } catch (e) {
      console.error('Script generation failed:', getApiErrorMessage(e, 'script generation failed'));
      setGenState(GEN_STATES.FAILED);
    }
  }, [selectedInsightId, genState]);

  const handleCopy = useCallback(() => {
    if (!script?.script) return;
    navigator.clipboard.writeText(script.script).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [script]);

  if (!insight) {
    return (
      <div className="orion-right" data-testid="orion-right-panel">
        <div className="orion-no-selection">
          <div style={{ fontSize: '12px', marginBottom: '4px' }}>AWAITING SELECTION</div>
          <div style={{ fontSize: '8px', color: '#52525B', letterSpacing: '1px' }}>Choose an insight from the feed to see assessment detail</div>
        </div>
      </div>
    );
  }

  const alertColor = LEVEL_COLORS[insight.alert] || '#00E5FF';
  const dataPoints = insight.data_points || {};

  const genBtnLabel = {
    [GEN_STATES.IDLE]: 'GENERATE BRIEFING',
    [GEN_STATES.GENERATING]: 'GENERATING...',
    [GEN_STATES.GENERATED]: 'REGENERATE',
    [GEN_STATES.FAILED]: 'RETRY BRIEFING',
  }[genState];

  return (
    <div className="orion-right" data-testid="orion-right-panel">
      <div className="orion-detail" data-testid="orion-insight-detail">
        {/* Header */}
        <div className="orion-detail-header">
          <div className="orion-detail-row1">
            <span className="orion-detail-badge" style={{ color: alertColor, borderColor: `${alertColor}40`, background: `${alertColor}10` }}>
              {insight.alert}
            </span>
            <span className="orion-detail-domain">{DOMAIN_LABELS[insight.domain] || insight.domain}</span>
            <button className="orion-compact-toggle" onClick={() => setCompactView(!compactView)} data-testid="compact-toggle">
              {compactView ? 'FULL' : 'BRIEF'}
            </button>
          </div>
          <div className="orion-detail-topic" data-testid="orion-detail-topic">{insight.topic}</div>
          <div className="orion-detail-ts">{insight.timestamp}</div>
        </div>

        {/* Compact mission brief view */}
        {compactView ? (
          <div className="orion-compact-brief" data-testid="orion-compact-brief">
            <div className="orion-compact-sev">
              <span style={{ color: alertColor }}>{insight.alert}</span>
              <span>{(insight.severity * 100).toFixed(0)}% sev</span>
              <span>{(insight.confidence * 100).toFixed(0)}% conf</span>
            </div>
            <div className="orion-compact-summary">{assessment?.summary || insight.summary}</div>
            {assessment?.impact && <div className="orion-compact-impact">{assessment.impact}</div>}
            <div className="orion-compact-sources">{insight.sources?.join(' / ')}</div>
          </div>
        ) : (
          <>
            {/* Data Points */}
            {Object.keys(dataPoints).length > 0 && (
              <div className="orion-detail-block" data-testid="orion-data-points">
                <div className="orion-section-label">DATA POINTS</div>
                <div className="orion-data-grid">
                  {Object.entries(dataPoints).map(([key, val]) => {
                    if (key === 'status' || key === 'message' || typeof val === 'object') return null;
                    const label = key.replace(/_/g, ' ').toUpperCase();
                    const displayVal = typeof val === 'number' ? (Number.isInteger(val) ? val : val.toFixed(2)) : String(val);
                    return (
                      <div key={key} className="orion-data-item">
                        <span className="orion-data-label">{label}</span>
                        <span className="orion-data-value">{displayVal}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Assessment */}
            {assessment && !assessment.error && (
              <div className="orion-detail-block" data-testid="orion-assessment">
                <div className="orion-section-label">ASSESSMENT</div>
                <div className="orion-assessment-state" style={{ color: LEVEL_COLORS[assessment.state] || '#00E5FF' }}>
                  {assessment.state}
                </div>
                <div className="orion-assessment-body">{assessment.summary}</div>
                {assessment.explanation && (
                  <div className="orion-assessment-explain">{assessment.explanation}</div>
                )}
                {assessment.impact && (
                  <div className="orion-assessment-impact">{assessment.impact}</div>
                )}
                <div className="orion-assessment-uncertainty">{assessment.uncertainty}</div>
                {assessment.knowledge_rules_applied?.length > 0 && (
                  <div className="orion-rules-applied">
                    {assessment.knowledge_rules_applied.map(r => (
                      <span key={r} className="orion-rule-tag">{r}</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Sources */}
            <div className="orion-detail-block">
              <div className="orion-section-label">SOURCES</div>
              <div className="orion-sources">
                {insight.sources?.map((s, i) => (
                  <span key={i} className="orion-src-tag" data-testid={`source-${s.replace(/\s/g, '-')}`}>{s}</span>
                ))}
              </div>
            </div>

            {/* Severity/Confidence meters */}
            <div className="orion-detail-block">
              <div className="orion-meters">
                <div className="orion-meter">
                  <span className="orion-meter-label">SEVERITY</span>
                  <div className="orion-bar-track">
                    <div className="orion-bar-fill" style={{ width: `${insight.severity * 100}%`, background: alertColor }} />
                  </div>
                  <span className="orion-meter-val" style={{ color: alertColor }}>{(insight.severity * 100).toFixed(0)}%</span>
                </div>
                <div className="orion-meter">
                  <span className="orion-meter-label">CONFIDENCE</span>
                  <div className="orion-bar-track">
                    <div className="orion-bar-fill" style={{ width: `${insight.confidence * 100}%`, background: '#00E5FF' }} />
                  </div>
                  <span className="orion-meter-val">{(insight.confidence * 100).toFixed(0)}%</span>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Script Generation */}
        <div className="orion-detail-block">
          <button
            className={`orion-gen-btn ${genState === GEN_STATES.FAILED ? 'failed' : ''}`}
            onClick={handleGenerateScript}
            disabled={genState === GEN_STATES.GENERATING}
            data-testid="orion-generate-script-btn"
          >
            {genBtnLabel}
          </button>

          {script && (
            <div className="orion-script-output" data-testid="orion-script-output">
              <div className="orion-script-text">{script.script}</div>
              {script.routing && (
                <div className="orion-routing-meta" data-testid="orion-routing-meta">
                  <span className="orion-routing-model" data-testid="orion-routing-model">
                    {script.routing.selectedModel}
                  </span>
                  <span className="orion-routing-sep">/</span>
                  <span className="orion-routing-reason" data-testid="orion-routing-reason">
                    {script.routing.routingReasonLabel}
                  </span>
                  {script.routing.fallbackUsed && (
                    <span className="orion-routing-fallback" data-testid="orion-routing-fallback">FALLBACK</span>
                  )}
                </div>
              )}
              <div className="orion-script-meta">
                <span>{script.knowledge_rules_applied?.length || 0} rules applied</span>
                <button className="orion-copy-btn" onClick={handleCopy} data-testid="orion-copy-btn">
                  {copied ? 'COPIED' : 'COPY'}
                </button>
              </div>
            </div>
          )}

          {genState === GEN_STATES.FAILED && !script && (
            <div className="orion-gen-error" data-testid="orion-gen-error">Generation failed. Retry or check source health.</div>
          )}
        </div>
      </div>
    </div>
  );
}
